import React, { FC, PropsWithChildren, createContext } from 'react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import { CLONE_TEMPLATE, DELETE_TEMPLATE } from 'graphql/mutations/template';

import { TemplatesType } from 'types/enums';
import { ClonePayloadProps } from '../../modals/CloneModal/interfaces';

interface TemplatesContextProps extends TemplatesContextProviderProps {
  deleteTemplate?: (id: string) => void;
  cloneTemplate?: (id: string, payload: ClonePayloadProps) => void;
}

interface TemplatesContextProviderProps {
  type: TemplatesType;
  queryKey: TemplatesListQueryKey;
}

export const TemplatesContext = createContext<TemplatesContextProps>(undefined!);

export const TemplatesContextProvider: FC<PropsWithChildren<{
  value: TemplatesContextProviderProps;
}>> = ({ children, value }) => {
  const navigate = useNavigate();

  const [cloneTemplate] = useMutation(CLONE_TEMPLATE, {
    onCompleted: ({ cloneTemplate }) => navigate(`/template/${cloneTemplate._id}/timeline`),
    refetchQueries: [
      value.queryKey,
      'getMyTemplates',
      'getTemplatesCount',
      'getTemplateTypesStats',
    ],
  });
  const [deleteTemplate] = useMutation(DELETE_TEMPLATE, {
    refetchQueries: [value.queryKey, 'getTemplatesCount', 'getTemplateTypesStats'],
  });

  const handleCloneTemplate = (templateId: string, payload: ClonePayloadProps) =>
    cloneTemplate({ variables: { templateId, payload } });

  const handleDeleteTemplate = (id: string) => deleteTemplate({ variables: { templateId: id } });

  return (
    <TemplatesContext.Provider
      value={{
        ...value,
        deleteTemplate: handleDeleteTemplate,
        cloneTemplate: handleCloneTemplate,
      }}
    >
      {children}
    </TemplatesContext.Provider>
  );
};
